import React, { useState, useEffect } from 'react';
import { Label, Stack, Text, TextField } from '@fluentui/react';
import Select from 'react-dropdown-select';
import CountryPrefixes from '@/utils/CountryPrefixes';
import { UserIdentityType } from '@/services/UserIdentitiesService';
import { UserType } from '@/services/UserService';
import DatePickerComponent from '../Elements/DatePickerComponent/DatePickerComponent';
import { PersonNameMaxLength } from '@/utils/personNameRules';
import { UserFormErrors, getInitialErrors, setFieldError, shouldDisableSubmit, validateBirthday, validateTextField } from './userFormValidation';
import { formatDisplayDate, formatDisplayPhone, formatDisplayValue } from './userFormDisplay';
import './Subscriptions.sass';

const stackTokens = { childrenGap: 50 };
const columnProps = { tokens: {childrenGap: 15}, styles: {root: {width: "100%"}}};

type TUserFormProps =
{
    user: UserType
    // Modo lectura: los datos se pintan como texto, sin validar ni deshabilitar el botón (cliente ya identificado)
    readOnly: boolean
    setIsButtonDisabled: (value: boolean) => void
    setUserIdentity: (value: UserIdentityType) => void
}

type TReadOnlyFieldProps =
{
    label: string
    value: string
}

// Dato en lectura (etiqueta + valor) sin ningún elemento que pueda recibir el foco: con el foco en un input
// de solo lectura, algunos navegadores (p. ej. Vivaldi) navegan a la página anterior al pulsar Retroceso.
const ReadOnlyField = (props: TReadOnlyFieldProps) =>
{
    return(
        <div>
            <Label>{props.label}</Label>
            <Text block>{props.value}</Text>
        </div>
    );
};

const UserForm = (props: TUserFormProps) =>
{
    const mainUserIdentity = props.user.mainUserIdentity;
    var country = CountryPrefixes.find((country) => country.phone_code === (mainUserIdentity?.countryPrefix ?? '+34'));

    const userInitialState: UserIdentityType  =
    {
        firstName: mainUserIdentity?.firstName || '',
        lastName: mainUserIdentity?.lastName || '',
        secondLastName: mainUserIdentity?.secondLastName || '',
        birthday: mainUserIdentity?.birthday ? new Date(mainUserIdentity.birthday) : undefined,
        // El email del interviniente principal si lo tiene (lectura, o principal NEW que la compra rellena) y, si no, el
        // del usuario de acceso. En el alta sin intervinientes la identidad llega sin email: siempre el de acceso.
        email: mainUserIdentity?.email || props.user.email || '',
        countryPrefix: country !== undefined ? country.phone_code : '+34',
        phone: mainUserIdentity?.phone || ''
    }

    const [userIdentity, setUserIdentity] = useState<UserIdentityType>(userInitialState);
    const [errors, setErrors] = useState<UserFormErrors>(() => props.readOnly ? {} : getInitialErrors(userInitialState));

    // En el alta o el relleno del principal NEW, si hay usuario de acceso el email no se edita (igual que antes): se
    // muestra como texto. El del principal NEW, si ya tenía uno, se corrige en Gestión de Intervinientes.
    const isEmailFixed = !!props.user.email;

    const formDateChange = (value: Date | null | undefined) =>
    {
        if(props.readOnly)
        {
            return;
        }

        const message = validateBirthday(value);
        setErrors((prevState) => setFieldError(prevState, 'birthday', message));

        if(message === undefined && value)
        {
            setUserIdentity(prevState =>
            ({
                ...prevState,
                birthday: value
            }));
        }
    };

    const formValChange = (e: any) =>
    {
        const name: string = e.target.name;
        const value: string = e.target.value;

        if(props.readOnly)
        {
            return;
        }

        const message = validateTextField(name, value);
        setErrors((prevState) => setFieldError(prevState, name, message));

        setUserIdentity(prevState =>
        ({
            ...prevState,
            [name]: value
        }));
    };

    // El padre recibe siempre el estado actual del formulario y si el botón debe estar deshabilitado
    useEffect(() =>
    {
        props.setIsButtonDisabled(props.readOnly ? false : shouldDisableSubmit(userIdentity, errors));
        props.setUserIdentity(userIdentity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdentity, errors, props.readOnly]);

    // Intro dentro de un campo no debe enviar el formulario ni recargar la página:
    // el envío real es el botón "Realizar pago" de Subscription.tsx, que está fuera de este formulario
    const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) =>
    {
        event.preventDefault();
    };

    if(props.readOnly)
    {
        // Cliente ya identificado: los datos como texto en una rejilla de 2 columnas iguales (3 filas), para que la
        // columna derecha empiece siempre en la misma X. En pantallas estrechas pasa a una columna (Subscriptions.sass).
        return(
            <div className="user-readonly-grid">
                <ReadOnlyField label="Nombre" value={formatDisplayValue(userIdentity.firstName)} />
                <ReadOnlyField label="Primer Apellido" value={formatDisplayValue(userIdentity.lastName)} />
                <ReadOnlyField label="Segundo Apellido" value={formatDisplayValue(userIdentity.secondLastName)} />
                <ReadOnlyField label="Fecha de nacimiento" value={formatDisplayDate(userIdentity.birthday)} />
                <ReadOnlyField label="Correo electrónico" value={formatDisplayValue(userIdentity.email)} />
                <ReadOnlyField label="Número teléfono móvil" value={formatDisplayPhone(mainUserIdentity?.countryPrefix, userIdentity.phone)} />
            </div>
        );
    }

    return(
        <form onSubmit={onFormSubmit} noValidate>
            <Stack tokens={stackTokens}>
                <Stack horizontal {...columnProps}>
                    <Stack.Item grow>
                        <TextField
                            label="Nombre"
                            required
                            placeholder="Ingresa el nombre"
                            name="firstName"
                            value={userIdentity.firstName}
                            onChange={formValChange}
                            errorMessage={errors.firstName}
                            maxLength={PersonNameMaxLength}
                        />
                    </Stack.Item>
                    <Stack.Item grow>
                        <TextField
                            label="Primer Apellido"
                            required
                            placeholder="Ingresa el primer apellido"
                            name="lastName"
                            value={userIdentity.lastName}
                            onChange={formValChange}
                            errorMessage={errors.lastName}
                            maxLength={PersonNameMaxLength}
                        />
                    </Stack.Item>
                </Stack>
                <Stack horizontal {...columnProps}>
                    <Stack.Item grow>
                        <TextField
                            label="Segundo Apellido"
                            placeholder="Ingresa el segundo apellido"
                            name="secondLastName"
                            value={userIdentity.secondLastName}
                            onChange={formValChange}
                            errorMessage={errors.secondLastName}
                            maxLength={PersonNameMaxLength}
                        />
                    </Stack.Item>
                    <Stack.Item grow>
                        <DatePickerComponent userIdentity={userIdentity} userInitial={userInitialState} formDateChange={formDateChange} />
                    </Stack.Item>
                </Stack>
                <Stack {...columnProps}>
                    {isEmailFixed &&
                    (
                        <ReadOnlyField label="Correo electrónico" value={formatDisplayValue(userIdentity.email)} />
                    )}
                    {!isEmailFixed &&
                    (
                        <TextField
                            label="Correo electrónico"
                            required
                            placeholder="Ingresa el correo electrónico"
                            name="email"
                            value={userIdentity.email}
                            onChange={formValChange}
                            errorMessage={errors.email}
                        />
                    )}
                </Stack>
                <Stack horizontal {...columnProps}>
                    <Stack.Item>
                        <label className="react-dropdown-select-label">Prefijo (país) *</label>
                        <Select labelField='name'
                            name="countryCode"
                            options={CountryPrefixes}
                            onChange={(value: any) => formValChange({ target: { name: 'countryPrefix', value: value[0]?.phone_code ?? '' } }) }
                            dropdownHeight='2000'
                            noDataLabel={'No encontrado'}
                            backspaceDelete={false}
                            values={CountryPrefixes.filter((countryPrefix) => countryPrefix.phone_code === userIdentity.countryPrefix)}
                            placeholder=''
                            itemRenderer={({item, methods}) =>
                            (
                                <span role="option"
                                    aria-selected="false"
                                    aria-label={item.name + ' ' + item.phone_code}
                                    // @ts-ignore
                                    tabIndex="-1"
                                    className="react-dropdown-select-item"
                                    onClick={() => methods.addItem(item)}
                                    key={'operation-country-prefix-' + item.iso3}
                                >
                                    {item.name + ' ' + item.phone_code}
                                </span>
                            )}
                            searchable={true}
                            searchBy="text"
                        />
                    </Stack.Item>
                    <Stack.Item grow={3}>
                        <TextField label="Número teléfono móvil"
                            required
                            placeholder="Introduzca el teléfono móvil"
                            name="phone"
                            value={userIdentity.phone}
                            onChange={formValChange}
                            errorMessage={errors.phone}
                        />
                    </Stack.Item>
                </Stack>
            </Stack>
        </form>
    );
};

export default UserForm;
